import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { leaderboardApi } from '../api/endpoints';
import type {
  LeaderboardAgenceDto, LeaderboardEntryDto, Metric,
  PalmaresMonthDetailDto, PalmaresMonthDto,
  Period, TodayActivityDto, TodayVenteDto, UserDto
} from '../api/types';
import { useToast } from '../hooks/useToast';
import { fmtEur, fmtK } from '../lib/format';

type Tab = 'ind' | 'ag' | 'palmares';
type LbPeriod = Extract<Period, 'month' | 'year'>;

const METRICS: { code: Metric; label: string }[] = [
  { code: 'score',      label: 'Score' },
  { code: 'commission', label: 'Commission' },
  { code: 'epargne',    label: 'Épargne' },
  { code: 'immo',       label: 'Immobilier' }
];

interface OutletCtx { user?: UserDto }

export function ClassementPage() {
  const { user } = useOutletContext<OutletCtx>();
  const toast = useToast();
  const [tab, setTab] = useState<Tab>('ind');
  const [metric, setMetric] = useState<Metric>('score');
  const [period, setPeriod] = useState<LbPeriod>('month');
  const [showScoreInfo, setShowScoreInfo] = useState(false);

  const [individual, setIndividual] = useState<LeaderboardEntryDto[]>([]);
  const [agences, setAgences]       = useState<LeaderboardAgenceDto[]>([]);
  const [palmaresMonths, setPalmaresMonths] = useState<PalmaresMonthDto[]>([]);
  const [todayActivity, setTodayActivity] = useState<TodayActivityDto | null>(null);
  const [showTodayModal, setShowTodayModal] = useState(false);
  const [loading, setLoading]       = useState(true);

  // ─── Drill-down agence (modal détail consultants) ───
  const [selectedAgence, setSelectedAgence] = useState<string | null>(null);
  const [agenceConsultants, setAgenceConsultants] = useState<LeaderboardEntryDto[]>([]);
  const [agenceDetailLoading, setAgenceDetailLoading] = useState(false);

  // ─── Drill-down palmarès mensuel (modal détail mois) ─
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [monthDetail, setMonthDetail] = useState<PalmaresMonthDetailDto | null>(null);
  const [monthDetailLoading, setMonthDetailLoading] = useState(false);

  async function openAgenceDetail(agence: string) {
    setSelectedAgence(agence);
    setAgenceDetailLoading(true);
    try {
      const all = await leaderboardApi.individual(metric, period);
      setAgenceConsultants(all.filter(e => e.agence === agence));
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erreur', 'error');
      setSelectedAgence(null);
    } finally {
      setAgenceDetailLoading(false);
    }
  }

  async function openMonthDetail(month: string) {
    setSelectedMonth(month);
    setMonthDetailLoading(true);
    setMonthDetail(null);
    try {
      const detail = await leaderboardApi.palmaresMonth(month);
      setMonthDetail(detail);
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Erreur', 'error');
      setSelectedMonth(null);
    } finally {
      setMonthDetailLoading(false);
    }
  }

  useEffect(() => {
    setLoading(true);
    const job: Promise<unknown> = tab === 'palmares'
      // L'historique du palmarès est indépendant du filtre période — c'est tous les mois passés
      ? leaderboardApi.palmaresHistory().then(setPalmaresMonths)
      : tab === 'ind'
        ? leaderboardApi.individual(metric, period).then(setIndividual)
        : leaderboardApi.agence(metric, period).then(setAgences);

    job
      .catch(err => toast(err instanceof Error ? err.message : 'Erreur', 'error'))
      .finally(() => setLoading(false));
  }, [tab, metric, period, toast]);

  // ─── Bandeau d'activité du jour (charge à part, à chaque visite de Classement) ─── 
  useEffect(() => {
    leaderboardApi.today()
      .then(setTodayActivity)
      .catch(() => { /* silencieux : le bandeau disparaît simplement */ });
  }, []);

  return (
    <div className="py-4 space-y-3">

      {/* ─── Filtres période ─── */}
      <nav className="grid grid-cols-2 gap-1 bg-surface-muted p-1 rounded-xl max-w-[260px]">
        {(['month', 'year'] as LbPeriod[]).map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`py-2 text-xs font-demi rounded-lg transition-colors
              ${period === p
                ? 'bg-white text-ink shadow-sm'
                : 'text-ink-sub hover:text-ink'}`}
          >
            {p === 'month' ? 'Mois' : 'Année'}
          </button>
        ))}
      </nav>

      {/* ─── Bandeau activité du jour (cliquable, toujours visible si data) ─── */}
      {todayActivity && (
        todayActivity.totalVentes > 0 ? (
          <button
            type="button"
            onClick={() => setShowTodayModal(true)}
            className="w-full flex items-center gap-2 bg-teal-bg border border-teal/30 rounded-xl px-4 py-2.5 text-sm hover:bg-teal-bg/70 transition-colors text-left"
          >
            <span className="w-2 h-2 rounded-full bg-teal animate-pulse shrink-0" />
            <span className="text-teal-dark flex-1">
              Aujourd'hui ·{' '}
              <strong>{todayActivity.totalVentes}</strong> vente{todayActivity.totalVentes > 1 ? 's' : ''}
              {' · '}
              <strong>{todayActivity.consultantCount}</strong> consultant{todayActivity.consultantCount > 1 ? 's' : ''} actif{todayActivity.consultantCount > 1 ? 's' : ''}
            </span>
            <span className="text-teal-dark text-xl leading-none">›</span>
          </button>
        ) : (
          <div className="bg-surface-muted border border-border rounded-xl px-4 py-2.5 text-sm text-ink-sub">
            Aucune vente déclarée aujourd'hui
          </div>
        )
      )}

      {/* ─── Tabs principaux ─── */}
      <div className="flex gap-1 border-b border-border">
        {([
          { code: 'ind',      label: 'Individuel' },
          { code: 'ag',       label: 'Agences' },
          { code: 'palmares', label: 'Palmarès' }
        ] as { code: Tab; label: string }[]).map(t => (
          <button
            key={t.code}
            onClick={() => setTab(t.code)}
            className={`px-3 py-2 text-sm font-demi -mb-px border-b-2 transition-colors
              ${tab === t.code
                ? 'border-teal text-teal-dark'
                : 'border-transparent text-ink-sub hover:text-ink'}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ─── Sélecteur métrique (sauf Palmarès) ─── */}
      {tab !== 'palmares' && (
        <div className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1">
          {METRICS.map(m => {
            const isScore = m.code === 'score';
            const sel = metric === m.code;
            return (
              <div key={m.code} className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => setMetric(m.code)}
                  className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap border transition-colors
                    ${sel
                      ? 'bg-teal text-white border-teal'
                      : 'bg-white text-ink-sub border-border hover:bg-surface-muted'}`}
                >
                  {m.label}
                </button>
                {isScore && (
                  <button
                    type="button"
                    onClick={() => setShowScoreInfo(true)}
                    className="w-5 h-5 rounded-full bg-surface-muted border border-border text-ink-sub text-[10px] font-mono hover:bg-teal-bg hover:text-teal-dark transition-colors"
                    title="Comment est calculé le score ?"
                    aria-label="Comment est calculé le score ?"
                  >
                    i
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Contenu selon tab ─── */}
      {loading ? (
        <div className="text-center text-ink-sub py-8">Chargement…</div>
      ) : tab === 'ind' ? (
        <IndividualBoard entries={individual} metric={metric} myUserId={user?.id} />
      ) : tab === 'ag' ? (
        <AgencesBoard
          entries={agences}
          metric={metric}
          onClickAgence={openAgenceDetail}
        />
      ) : (
        <PalmaresBoard
          months={palmaresMonths}
          myUserId={user?.id}
          onClickMonth={openMonthDetail}
        />
      )}

      {/* ─── Modal info score ─── */}
      {showScoreInfo && (
        <ScoreInfoModal onClose={() => setShowScoreInfo(false)} />
      )}

      {/* ─── Modal activité du jour ─── */}
      {showTodayModal && todayActivity && (
        <TodayActivityModal
          activity={todayActivity}
          onClose={() => setShowTodayModal(false)}
        />
      )}

      {/* ─── Modal drill-down agence ─── */}
      {selectedAgence && (
        <AgenceDetailModal
          agence={selectedAgence}
          consultants={agenceConsultants}
          metric={metric}
          loading={agenceDetailLoading}
          myUserId={user?.id}
          onClose={() => setSelectedAgence(null)}
        />
      )}

      {/* ─── Modal drill-down mois (palmarès) ─── */}
      {selectedMonth && (
        <MonthDetailModal
          month={selectedMonth}
          detail={monthDetail}
          loading={monthDetailLoading}
          myUserId={user?.id}
          onClose={() => {
            setSelectedMonth(null);
            setMonthDetail(null);
          }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Tab : Classement individuel (avec podium)
// ───────────────────────────────────────────────────────────────────

function IndividualBoard({
  entries, metric, myUserId
}: { entries: LeaderboardEntryDto[]; metric: Metric; myUserId?: string }) {
  if (entries.length === 0) {
    return <EmptyBoard message="Le classement se construit au fil des déclarations" />;
  }

  const myEntry = myUserId ? entries.find(e => e.userId === myUserId) : undefined;
  const top3 = entries.slice(0, 3);
  const rest = entries.slice(3);

  return (
    <div className="space-y-3">
      {/* Ma position si je suis classé */}
      {myEntry && (
        <MyPosition entry={myEntry} metric={metric} />
      )}

      {/* Podium */}
      {top3.length >= 2 && <Podium top={top3} metric={metric} />}

      {/* Liste à partir de la 4e place */}
      {rest.length > 0 && (
        <ul className="space-y-1.5">
          {rest.map((e, idx) => (
            <LbRow
              key={e.userId}
              entry={e}
              rank={idx + 4}
              metric={metric}
              isMe={e.userId === myUserId}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function MyPosition({
  entry, metric
}: { entry: LeaderboardEntryDto; metric: Metric }) {
  const isPodium = entry.rank <= 3;
  const medal = entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : '🎯';
  const initials = getInitials(entry.consultantName);

  return (
    <div className="flex items-center gap-3 bg-white border border-teal/30 rounded-xl px-3 py-2.5">
      <div className="text-2xl font-bold text-teal-dark min-w-[40px]">#{entry.rank}</div>
      <div className="w-11 h-11 rounded-full bg-surface-muted border border-border flex items-center justify-center text-sm font-demi text-ink-sub shrink-0 overflow-hidden">
        {entry.photoUrl
          ? <img src={entry.photoUrl} alt="" className="w-full h-full object-cover" />
          : <span>{initials}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-demi text-ink truncate">
          {entry.consultantName} <span className="text-xs text-teal-dark">(vous)</span>
        </div>
        <div className="text-xs text-ink-sub">
          {entry.agence} · {fmtMetric(entry.value, metric)} · {entry.nbVentes} vente{entry.nbVentes > 1 ? 's' : ''}
        </div>
      </div>
      <div className="text-2xl">{isPodium ? medal : '🎯'}</div>
    </div>
  );
}

function Podium({ top, metric }: { top: LeaderboardEntryDto[]; metric: Metric }) {
  // Ordre visuel : 2e à gauche, 1er au centre, 3e à droite
  const visual: number[] = [];
  if (top[1]) visual.push(1);
  visual.push(0);
  if (top[2]) visual.push(2);

  const crowns = ['🥇', '🥈', '🥉'];
  // Couleurs et hauteurs : 1=or 2=gris 3=teal
  const heights = ['h-20', 'h-14', 'h-10']; // 1er plus haut
  const styles = [
    'bg-gradient-to-b from-srx/20 to-srx/5 border-srx/40 text-srx-dark',
    'bg-gradient-to-b from-qts/10 to-qts/3 border-qts/30 text-qts',
    'bg-gradient-to-b from-teal/15 to-teal/3 border-teal/30 text-teal-dark'
  ];
  const valueColors = ['text-srx-dark', 'text-qts', 'text-teal-dark'];

  return (
    <div className="flex items-end justify-center gap-2 mb-2">
      {visual.map(i => {
        const e = top[i];
        const initials = getInitials(e.consultantName);
        return (
          <div key={e.userId} className="flex-1 max-w-[110px] flex flex-col items-center gap-1">
            <div className={`w-full rounded-t-lg border border-b-0 ${styles[i]} ${heights[i]} flex flex-col items-center justify-end p-1.5`}>
              <div className="text-xl leading-none">{crowns[i]}</div>
              <div className="font-mono text-xs font-demi">#{i + 1}</div>
            </div>
            <div className="w-9 h-9 rounded-full bg-surface-muted border border-border flex items-center justify-center text-xs font-demi text-ink-sub -mt-2 overflow-hidden">
              {e.photoUrl
                ? <img src={e.photoUrl} alt="" className="w-full h-full object-cover" />
                : <span>{initials}</span>}
            </div>
            <div className="text-[11px] font-demi text-ink text-center max-w-full truncate w-full px-1">
              {e.consultantName}
            </div>
            <div className={`font-mono text-[10px] font-demi whitespace-nowrap ${valueColors[i]}`}>
              {fmtMetricCompact(e.value, metric, e.nbVentes)}
            </div>
            <div className="text-[10px] text-ink-sub font-light">{e.agence}</div>
          </div>
        );
      })}
    </div>
  );
}

function LbRow({
  entry, rank, metric, isMe
}: { entry: LeaderboardEntryDto; rank: number; metric: Metric; isMe: boolean }) {
  const initials = getInitials(entry.consultantName);
  return (
    <li className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border
      ${isMe ? 'bg-teal-bg border-teal/30' : 'bg-white border-border'}`}>
      <div className="font-mono text-sm font-demi text-ink-dim min-w-[24px]">{rank}</div>
      <div className="w-8 h-8 rounded-full bg-surface-muted border border-border flex items-center justify-center text-[11px] font-demi text-ink-sub shrink-0 overflow-hidden">
        {entry.photoUrl
          ? <img src={entry.photoUrl} alt="" className="w-full h-full object-cover" />
          : <span>{initials}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-demi text-ink truncate leading-tight">
          {entry.consultantName}
          {isMe && <span className="text-[10px] text-teal-dark ml-1 font-regular">(vous)</span>}
        </div>
        <div className="text-xs text-ink-sub mt-1 font-light">{entry.agence}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm font-demi text-ink whitespace-nowrap">
          {fmtMetric(entry.value, metric)}
        </div>
        <div className="font-mono text-[10px] text-ink-dim whitespace-nowrap mt-0.5 font-light">
          {entry.nbVentes} vente{entry.nbVentes > 1 ? 's' : ''}
        </div>
      </div>
    </li>
  );
}

// ───────────────────────────────────────────────────────────────────
// Tab : Classement Agences
// ───────────────────────────────────────────────────────────────────

function AgencesBoard({
  entries, metric, onClickAgence
}: {
  entries: LeaderboardAgenceDto[];
  metric: Metric;
  onClickAgence: (agence: string) => void;
}) {
  if (entries.length === 0) {
    return <EmptyBoard message="Aucune agence à classer pour le moment" />;
  }
  const maxV = entries[0]?.value ?? 1;
  const medals = ['🥇', '🥈', '🥉'];

  return (
    <ul className="space-y-1.5">
      {entries.map((e, idx) => {
        const pct = Math.round((e.value / maxV) * 100);
        return (
          <li key={e.agence}>
            <button
              type="button"
              onClick={() => onClickAgence(e.agence)}
              className="w-full text-left bg-white border border-border rounded-xl px-3 py-2.5 hover:bg-surface-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="text-xl min-w-[28px] text-center">
                  {idx < 3 ? medals[idx] : `${idx + 1}.`}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-demi text-ink">{e.agence}</div>
                  <div className="text-xs text-ink-sub mt-0.5 font-light">
                    {e.nbConsultants} consultant{e.nbConsultants > 1 ? 's' : ''} ·
                    {' '}{e.nbVentes} vente{e.nbVentes > 1 ? 's' : ''}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono text-sm font-demi text-ink">
                    {fmtMetric(e.value, metric)}
                  </div>
                </div>
                <span className="text-ink-dim text-lg leading-none">›</span>
              </div>
              <div className="h-[3px] bg-surface-muted rounded-full mt-2 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${pct}%`,
                    background: 'linear-gradient(90deg, #C5A46A, #324553)'
                  }}
                />
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ───────────────────────────────────────────────────────────────────
// Tab : Palmarès — Historique des mois passés
// ───────────────────────────────────────────────────────────────────

function PalmaresBoard({
  months, myUserId, onClickMonth
}: {
  months: PalmaresMonthDto[];
  myUserId?: string;
  onClickMonth: (month: string) => void;
}) {
  if (months.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="text-4xl mb-3">📅</div>
        <div className="text-sm text-ink-sub leading-relaxed">
          Aucun mois archivé pour l'instant<br />
          <span className="text-xs font-light">Le palmarès apparaît dès le mois suivant</span>
        </div>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {months.map(m => {
        const w = m.winner;
        const isMeWinner = w?.userId === myUserId;
        return (
          <li key={m.month}>
            <button
              type="button"
              onClick={() => onClickMonth(m.month)}
              className="w-full text-left bg-white border border-border rounded-xl px-3 py-3 hover:bg-surface-muted/50 transition-colors flex items-center gap-3"
            >
              {/* Mois / Année à gauche */}
              <div className="bg-surface-muted rounded-lg px-2.5 py-2 text-center min-w-[60px]">
                <div className="text-[10px] uppercase tracking-wider text-ink-sub font-mono">
                  {m.monthLabel.slice(0, 3)}
                </div>
                <div className="text-sm text-ink font-demi font-mono mt-0.5">
                  {m.year}
                </div>
              </div>

              {/* Bloc winner + stats */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 text-sm font-demi text-ink truncate">
                  <span>🥇</span>
                  <span className="truncate">
                    {w?.consultantName ?? '—'}
                    {isMeWinner && <span className="text-[10px] text-teal-dark ml-1 font-regular">(vous)</span>}
                  </span>
                </div>
                <div className="text-[11.5px] text-ink-sub font-light mt-0.5">
                  {m.nbVentes} vente{m.nbVentes > 1 ? 's' : ''} ·
                  {' '}{m.nbConsultants} consultant{m.nbConsultants > 1 ? 's' : ''} ·
                  {' '}{fmtK(m.totalCollecte)} ·
                  {' '}{fmtEur(m.totalCommission)}
                </div>
              </div>

              <span className="text-ink-dim text-lg leading-none">›</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// ───────────────────────────────────────────────────────────────────
// Modal d'explication du score
// ───────────────────────────────────────────────────────────────────

function ScoreInfoModal({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
      role="dialog" aria-modal="true"
      aria-labelledby="score-info-title"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 id="score-info-title" className="text-base font-demi text-ink">
            Calcul du score
          </h2>
          <button onClick={onClose} className="text-ink-sub hover:text-ink text-xl leading-none" aria-label="Fermer">✕</button>
        </div>

        <p className="text-sm text-ink-sub mb-4 leading-relaxed">
          Le score récompense à la fois <strong className="text-ink">l'activité</strong>,
          la <strong className="text-ink">valeur générée</strong> et
          le <strong className="text-ink">volume traité</strong>.
        </p>

        <div className="bg-teal-bg border border-teal/30 rounded-lg px-4 py-3 font-mono text-sm text-teal-dark text-center mb-4">
          score = (com × 0,5) + (collecte × 0,001) + 10
        </div>

        <div className="space-y-3 text-sm">
          <ScoreItem
            badge="+10" badgeBg="bg-qts-bg text-qts"
            title="Bonus de base"
            desc="Pour chaque vente déclarée — récompense l'activité."
          />
          <ScoreItem
            badge="×0,5" badgeBg="bg-srx-bg text-srx-dark"
            title="Sur la commission"
            desc="+0,5 pt par € de commission générée."
          />
          <ScoreItem
            badge="×0,001" badgeBg="bg-teal-bg text-teal-dark"
            title="Sur la collecte"
            desc={<>+0,001 pt par € collecté ou prix immo.<br /><em className="text-xs">Pour les Primes Périodiques, la collecte est annualisée (versement mensuel × 12).</em></>}
          />
        </div>

        <div className="bg-surface-muted border border-border rounded-lg px-3 py-2.5 mt-4 text-xs text-ink-sub leading-relaxed">
          <strong className="text-ink">Exemple :</strong> PP de 100 € / mois (frais 4%, MIA) — collecte annuelle 1 200 €, commission 250 €.<br />
          Score = (250 × 0,5) + (1 200 × 0,001) + 10 = <strong className="text-srx-dark">136 pts</strong>.
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full mt-5 py-2.5 rounded-xl bg-surface-muted text-ink-sub hover:bg-border hover:text-ink text-sm transition-colors"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}

function ScoreItem({
  badge, badgeBg, title, desc
}: { badge: string; badgeBg: string; title: string; desc: React.ReactNode }) {
  return (
    <div className="flex gap-3 items-start">
      <div className={`w-9 h-9 rounded-full flex items-center justify-center font-mono text-[10px] font-demi shrink-0 ${badgeBg}`}>
        {badge}
      </div>
      <div className="flex-1">
        <div className="font-demi text-ink">{title}</div>
        <div className="text-ink-sub text-xs leading-relaxed mt-0.5">{desc}</div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Modal "Activité du jour"
// ───────────────────────────────────────────────────────────────────

function TodayActivityModal({
  activity, onClose
}: { activity: TodayActivityDto; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Récap par consultant : nom → nb ventes, total commission
  const byConsultant = useMemo(() => {
    const map = new Map<string, { name: string; nb: number; com: number; photoUrl: string | null }>();
    activity.ventes.forEach(v => {
      const cur = map.get(v.consultantName) ?? { name: v.consultantName, nb: 0, com: 0, photoUrl: v.consultantPhotoUrl };
      cur.nb += 1;
      cur.com += v.commission;
      map.set(v.consultantName, cur);
    });
    return Array.from(map.values()).sort((a, b) => b.com - a.com);
  }, [activity]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
      role="dialog" aria-modal="true"
      aria-labelledby="today-modal-title"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-teal animate-pulse" />
            <h2 id="today-modal-title" className="text-base font-demi text-ink">
              Activité du jour
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-ink-sub hover:text-ink text-xl leading-none"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
        <div className="text-xs text-ink-sub mb-4">
          {activity.totalVentes} vente{activity.totalVentes > 1 ? 's' : ''} ·
          {' '}{activity.consultantCount} consultant{activity.consultantCount > 1 ? 's' : ''} actif{activity.consultantCount > 1 ? 's' : ''}
        </div>

        {/* Récap par consultant */}
        {byConsultant.length > 0 && (
          <div className="mb-4">
            <div className="text-[11px] uppercase tracking-wider text-ink-sub font-demi mb-2">
              Par consultant
            </div>
            <ul className="space-y-1.5">
              {byConsultant.map(c => (
                <li key={c.name} className="flex items-center gap-3 bg-surface-muted rounded-lg px-3 py-2">
                  <div className="w-8 h-8 rounded-full bg-white border border-border flex items-center justify-center text-[11px] font-demi text-ink-sub overflow-hidden shrink-0">
                    {c.photoUrl
                      ? <img src={c.photoUrl} alt="" className="w-full h-full object-cover" />
                      : <span>{getInitials(c.name)}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-demi text-ink truncate">{c.name}</div>
                    <div className="text-xs text-ink-sub">
                      {c.nb} vente{c.nb > 1 ? 's' : ''}
                    </div>
                  </div>
                  <div className="font-mono text-sm font-demi text-teal-dark whitespace-nowrap">
                    {fmtEur(c.com)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Liste détaillée des ventes */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-ink-sub font-demi mb-2">
            Détail des ventes
          </div>
          <ul className="space-y-1.5">
            {activity.ventes.map(v => <TodayVenteRow key={v.id} v={v} />)}
          </ul>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="w-full mt-5 py-2.5 rounded-xl bg-surface-muted text-ink-sub hover:bg-border hover:text-ink text-sm transition-colors"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}

function TodayVenteRow({ v }: { v: TodayVenteDto }) {
  const time = new Date(v.createdAt).toLocaleTimeString('fr-FR', {
    hour: '2-digit', minute: '2-digit'
  });
  return (
    <li className="flex items-center gap-3 bg-white border border-border rounded-lg px-3 py-2">
      <div className={`text-[9px] font-demi px-1.5 py-0.5 rounded shrink-0 ${
        v.entite === 'srx' ? 'bg-teal-bg text-teal-dark' : 'bg-qts-bg text-qts'
      }`}>
        {v.entite === 'srx' ? 'SRX' : 'QUIN'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-ink truncate">
          <span className="font-demi">{v.consultantName}</span>
          <span className="text-ink-sub"> · {v.prodLabel}</span>
        </div>
        <div className="text-[11px] text-ink-sub">{v.agence} · {fmtK(v.montant)}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-xs font-demi text-teal-dark whitespace-nowrap">
          {fmtEur(v.commission)}
        </div>
        <div className="text-[10px] text-ink-dim">{time}</div>
      </div>
    </li>
  );
}

// ───────────────────────────────────────────────────────────────────
// Modal "Détail agence" — drill-down depuis l'onglet Agences
// ───────────────────────────────────────────────────────────────────

function AgenceDetailModal({
  agence, consultants, metric, loading, myUserId, onClose
}: {
  agence: string;
  consultants: LeaderboardEntryDto[];
  metric: Metric;
  loading: boolean;
  myUserId?: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // Stats agrégées : on a déjà chaque consultant avec nbVentes ; on n'a pas
  // directement la collecte ni la commission, donc on présente le minimum utile.
  // La somme nbVentes et nb consultants suffisent pour le sous-titre.
  const totalVentes = consultants.reduce((s, c) => s + c.nbVentes, 0);

  // Si la métrique est "commission", on peut sommer values
  const totalCom = metric === 'commission'
    ? consultants.reduce((s, c) => s + c.value, 0)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
      role="dialog" aria-modal="true"
      aria-labelledby="agence-modal-title"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 id="agence-modal-title" className="text-lg font-demi text-ink">
            {agence}
          </h2>
          <button
            onClick={onClose}
            className="text-ink-sub hover:text-ink text-xl leading-none"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
        <div className="text-xs text-ink-sub mb-4 font-light">
          {consultants.length} consultant{consultants.length > 1 ? 's' : ''} ·
          {' '}{totalVentes} vente{totalVentes > 1 ? 's' : ''}
          {totalCom != null && ` · ${fmtEur(totalCom)} de commission`}
        </div>

        {loading ? (
          <div className="text-center text-ink-sub py-8">Chargement…</div>
        ) : consultants.length === 0 ? (
          <div className="text-center text-ink-sub py-8 text-sm">
            Aucun consultant classé sur cette période
          </div>
        ) : (
          <ul className="space-y-1.5">
            {consultants.map((c, i) => <AgenceConsultantRow
              key={c.userId}
              entry={c}
              localRank={i + 1}
              metric={metric}
              isMe={c.userId === myUserId}
            />)}
          </ul>
        )}

        <button
          type="button"
          onClick={onClose}
          className="w-full mt-5 py-2.5 rounded-xl bg-surface-muted text-ink-sub hover:bg-border hover:text-ink text-sm transition-colors"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}

function AgenceConsultantRow({
  entry, localRank, metric, isMe
}: {
  entry: LeaderboardEntryDto;
  localRank: number;
  metric: Metric;
  isMe: boolean;
}) {
  const initials = getInitials(entry.consultantName);
  const medal = localRank === 1 ? '🥇' : localRank === 2 ? '🥈' : localRank === 3 ? '🥉' : null;

  return (
    <li className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border
      ${isMe ? 'bg-teal-bg border-teal/30' : 'bg-white border-border'}`}>
      <div className="min-w-[28px] text-center">
        {medal ? <span className="text-lg">{medal}</span> : (
          <span className="font-mono text-xs text-ink-dim">#{localRank}</span>
        )}
      </div>
      <div className="w-8 h-8 rounded-full bg-surface-muted border border-border flex items-center justify-center text-[11px] font-demi text-ink-sub shrink-0 overflow-hidden">
        {entry.photoUrl
          ? <img src={entry.photoUrl} alt="" className="w-full h-full object-cover" />
          : <span>{initials}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-demi text-ink truncate leading-tight">
          {entry.consultantName}
          {isMe && <span className="text-[10px] text-teal-dark ml-1 font-regular">(vous)</span>}
        </div>
        <div className="text-xs text-ink-sub mt-0.5 font-light">
          {entry.nbVentes} vente{entry.nbVentes > 1 ? 's' : ''}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm font-demi text-ink whitespace-nowrap">
          {fmtMetric(entry.value, metric)}
        </div>
      </div>
    </li>
  );
}

// ───────────────────────────────────────────────────────────────────
// Modal "Palmarès — Mois" — drill-down depuis l'onglet Palmarès
// ───────────────────────────────────────────────────────────────────

type MonthSubTab = 'ind' | 'ag';

function MonthDetailModal({
  month, detail, loading, myUserId, onClose
}: {
  month: string;
  detail: PalmaresMonthDetailDto | null;
  loading: boolean;
  myUserId?: string;
  onClose: () => void;
}) {
  const [subTab, setSubTab] = useState<MonthSubTab>('ind');
  const [metric, setMetric] = useState<Metric>('score');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const title = detail
    ? `Palmarès — ${detail.monthLabel} ${detail.year}`
    : `Palmarès — ${month}`;

  const indEntries = detail?.individualByMetric[metric] ?? [];
  const agEntries  = detail?.agenceByMetric[metric]     ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40"
      onClick={onClose}
      role="dialog" aria-modal="true"
      aria-labelledby="month-modal-title"
    >
      <div
        className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 id="month-modal-title" className="text-lg font-demi text-ink">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-ink-sub hover:text-ink text-xl leading-none"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        {/* Sub-tabs Top 10 / Agences */}
        <div className="grid grid-cols-2 gap-1 bg-surface-muted p-1 rounded-xl mb-3">
          <button
            type="button"
            onClick={() => setSubTab('ind')}
            className={`py-2 text-xs rounded-lg transition-colors
              ${subTab === 'ind' ? 'bg-white text-ink shadow-sm font-demi' : 'text-ink-sub hover:text-ink'}`}
          >
            Top 10
          </button>
          <button
            type="button"
            onClick={() => setSubTab('ag')}
            className={`py-2 text-xs rounded-lg transition-colors
              ${subTab === 'ag' ? 'bg-white text-ink shadow-sm font-demi' : 'text-ink-sub hover:text-ink'}`}
          >
            Agences
          </button>
        </div>

        {/* Pills métrique */}
        <div className="flex items-center gap-1.5 overflow-x-auto -mx-1 px-1 mb-3">
          {METRICS.map(m => {
            const sel = metric === m.code;
            return (
              <button
                key={m.code}
                onClick={() => setMetric(m.code)}
                className={`px-2.5 py-1 rounded-full text-xs whitespace-nowrap border transition-colors shrink-0
                  ${sel
                    ? 'bg-teal text-white border-teal'
                    : 'bg-white text-ink-sub border-border hover:bg-surface-muted'}`}
              >
                {m.label}
              </button>
            );
          })}
        </div>

        {/* Contenu */}
        {loading ? (
          <div className="text-center text-ink-sub py-8">Chargement…</div>
        ) : !detail ? (
          <div className="text-center text-ink-sub py-8 text-sm">
            Aucune donnée pour ce mois
          </div>
        ) : subTab === 'ind' ? (
          indEntries.length === 0 ? (
            <div className="text-center text-ink-sub py-8 text-sm">Aucun classé</div>
          ) : (
            <ul className="space-y-1.5">
              {indEntries.map((e, i) => (
                <MonthDetailIndRow
                  key={e.userId}
                  entry={e}
                  localRank={i + 1}
                  metric={metric}
                  isMe={e.userId === myUserId}
                />
              ))}
            </ul>
          )
        ) : (
          agEntries.length === 0 ? (
            <div className="text-center text-ink-sub py-8 text-sm">Aucune agence</div>
          ) : (
            <ul className="space-y-1.5">
              {agEntries.map((e, i) => (
                <MonthDetailAgRow key={e.agence} entry={e} localRank={i + 1} metric={metric} />
              ))}
            </ul>
          )
        )}

        <button
          type="button"
          onClick={onClose}
          className="w-full mt-5 py-2.5 rounded-xl bg-surface-muted text-ink-sub hover:bg-border hover:text-ink text-sm transition-colors"
        >
          Fermer
        </button>
      </div>
    </div>
  );
}

function MonthDetailIndRow({
  entry, localRank, metric, isMe
}: {
  entry: LeaderboardEntryDto;
  localRank: number;
  metric: Metric;
  isMe: boolean;
}) {
  const initials = getInitials(entry.consultantName);
  const medal = localRank === 1 ? '🥇' : localRank === 2 ? '🥈' : localRank === 3 ? '🥉' : null;

  return (
    <li className={`flex items-center gap-3 rounded-xl px-3 py-2.5 border
      ${isMe ? 'bg-teal-bg border-teal/30' : 'bg-white border-border'}`}>
      <div className="min-w-[28px] text-center">
        {medal ? <span className="text-lg">{medal}</span> : (
          <span className="font-mono text-xs text-ink-dim">#{localRank}</span>
        )}
      </div>
      <div className="w-8 h-8 rounded-full bg-surface-muted border border-border flex items-center justify-center text-[11px] font-demi text-ink-sub shrink-0 overflow-hidden">
        {entry.photoUrl
          ? <img src={entry.photoUrl} alt="" className="w-full h-full object-cover" />
          : <span>{initials}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-demi text-ink truncate leading-tight">
          {entry.consultantName}
          {isMe && <span className="text-[10px] text-teal-dark ml-1 font-regular">(vous)</span>}
        </div>
        <div className="text-xs text-ink-sub mt-0.5 font-light">
          {entry.agence} · {entry.nbVentes} vente{entry.nbVentes > 1 ? 's' : ''}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm font-demi text-ink whitespace-nowrap">
          {fmtMetric(entry.value, metric)}
        </div>
      </div>
    </li>
  );
}

function MonthDetailAgRow({
  entry, localRank, metric
}: { entry: LeaderboardAgenceDto; localRank: number; metric: Metric }) {
  const medal = localRank === 1 ? '🥇' : localRank === 2 ? '🥈' : localRank === 3 ? '🥉' : null;

  return (
    <li className="flex items-center gap-3 bg-white border border-border rounded-xl px-3 py-2.5">
      <div className="min-w-[28px] text-center">
        {medal ? <span className="text-lg">{medal}</span> : (
          <span className="font-mono text-xs text-ink-dim">#{localRank}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-demi text-ink truncate">{entry.agence}</div>
        <div className="text-xs text-ink-sub mt-0.5 font-light">
          {entry.nbConsultants} consultant{entry.nbConsultants > 1 ? 's' : ''} ·
          {' '}{entry.nbVentes} vente{entry.nbVentes > 1 ? 's' : ''}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-mono text-sm font-demi text-ink whitespace-nowrap">
          {fmtMetric(entry.value, metric)}
        </div>
      </div>
    </li>
  );
}

// ───────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────

function EmptyBoard({ message }: { message: string }) {
  return (
    <div className="text-center py-10">
      <div className="text-4xl mb-3">🏆</div>
      <div className="text-sm text-ink-sub">{message}</div>
    </div>
  );
}

function fmtMetric(val: number, metric: Metric): string {
  if (metric === 'score') return `${val} pts`;
  return fmtEur(val);
}

function fmtMetricCompact(val: number, metric: Metric, nbVentes: number): string {
  if (metric === 'score') return `${val} pts · ${nbVentes} vt${nbVentes > 1 ? 's' : ''}`;
  return fmtK(val);
}

function getInitials(name: string): string {
  return name.split(' ').filter(Boolean).slice(0, 2)
    .map(s => s.charAt(0).toUpperCase()).join('');
}
